import React, { useState, useEffect } from 'react';
import { Table, ConfigProvider, Tooltip, Modal, Form, message, Select } from 'antd';
import { throttle, displayText, emptyTemplate, formatDateString, isEqualArray } from 'utils';
import { useAutoSize } from 'hooks';
import { useSelector, useDispatch } from 'react-redux';
import PaginationComponent from '../../../components/pagination';
import * as Permissions from '../../../constants/permissions';
import { updateUserProfile } from '../../../redux/user';
import { localize } from '../../../locales';
import * as api from '../../../api';

import './index.css';
const User = () => {
	const [form] = Form.useForm();
	const [pageSize, setPageSize] = useState(20);
	const [pageIndex, setPageIndex] = useState(1);
	const [pageList, setPageList] = useState([]);
	const [total, setTotal] = useState(0);

	const [editingUser, setEditingUser] = useState(null);
	const [applications, setApplications] = useState([]);
	const [roles, setRoles] = useState([]);

	const [ref, tableHeight] = useAutoSize();
	console.log(tableHeight)

	const dispatch = useDispatch();
	const loginUser = useSelector((state) => {
		return state.user;
	});

	const [showEditModal, setShowEditModal] = useState(false);

	useEffect(() => {
		getRoleAndApplication();
	}, []);

	useEffect(() => {
		document.documentElement.scrollTop = 0;
		getPageData(pageIndex);
	}, [pageIndex, pageSize]);

	// MARK: - data provider of table
	const _columns = [
		{
			title: localize('account'),
			dataIndex: 'name',
			key: 'name',
			ellipsis: {
				showTitle: true,
			},
			width: 150,
			render: (account) => (
				<Tooltip placement="topLeft" title={account}>
					<span className="app-box-cursor-common">{displayText(account)}</span>
				</Tooltip>
			),
		},
		{
			title: localize('email'),
			dataIndex: 'email',
			key: 'email',
			ellipsis: {
				showTitle: true,
			},
			width: 150,
			render: (email) => (
				<Tooltip placement="topLeft" title={email}>
					<span className="app-box-cursor-common">{displayText(email)}</span>
				</Tooltip>
			),
		},
		{
			title: localize('createDate'),
			dataIndex: 'createdAt',
			key: 'createdAt',
			width: 150,
			render: (text) => {
				return <div className="app-list-date-style">{formatDateString(text)}</div>;
			},
		},
		{
			width: 1,
			render: () => (
				<div className="action-btn-con">
					<div className="action-icon-size-all-con"></div>
				</div>
			),
		},
		{
			title: localize('action'),
			key: 'action',
			width: 100,
			fixed: 'right',
			render: (_, record) => (
				<div className="action-btn-con">
					<div className="action-icon-size-all-con" onClick={() => throttle(editUser, 500)(record)}>
						<img className="action-icon-size-all" src={require('assets/images/editRoleIcon.png')} />
					</div>
				</div>
			),
		},
	];

	const mainColumns = () => {
		// return columns;
		const clms = [..._columns];
		if (!loginUser.permissions.includes(Permissions.Users_EDIT)) {
			clms.pop();
		}
		return clms;
	};
	// MARL: - api call
	const getPageData = (pageIndex) => {
		const payload = {
			pageSize: pageSize,
			pageIndex: pageIndex,
		};
		api.getUsers(payload).then((res) => {
			setPageList(res.result);
			setTotal(res.total);
		});
	};

	function getRoleAndApplication() {
		api.getApplications().then((res) => {
			let applications = res ?? [];
			applications = applications.map((item) => ({
				label: item.applicationName,
				value: item.id,
			}));
			setApplications(applications);
		});

		api.getUserRoles().then((res) => {
			let roles = res ?? [];
			roles = roles.map((item) => ({
				label: item.role,
				value: item.id,
			}));
			setRoles(roles);
		});
	}

	const updateUserRolesAndApps = async () => {
		try {
			const values = await form.validateFields();
			const { roleIdList, applicationIdList } = values;

			const isRoleChanged = !isEqualArray(roles, roleIdList);
			const isAppChanged = !isEqualArray(applications, applicationIdList);

			if (!isRoleChanged && !isAppChanged) {
				setShowEditModal(false);
				return;
			}
			const promises = [];
			if (isRoleChanged) {
				promises.push(api.updateUserRoles({ userId: editingUser.id, roles: roleIdList }));
			}
			if (isAppChanged) {
				promises.push(api.updateUserApplications({ userId: editingUser.id, applications: applicationIdList }));
			}
			const results = await Promise.allSettled(promises);
			const allSuccess = results.every((result) => result.status === 'fulfilled' && result.value.message === 'success');
			if (allSuccess) {
				setShowEditModal(false);
				pageIndex === 1 ? getPageData(1) : setPageIndex(1);
				document.documentElement.scrollTop = 0;
				form.resetFields();
				message.success(localize('updateSuccess'));
				// for test
				if (editingUser.id === loginUser.id) {
					dispatch(updateUserProfile(editingUser.id));
				}
			} else {
				console.error('Requests failed:', results);
			}
		} catch (error) {
			console.error('Something went wrong', error);
		}
	};
	const roleCancel = () => {
		setShowEditModal(false);
	};

	//
	const editUser = (record) => {
		setEditingUser(record);
		setShowEditModal(true);
		const valuesObj = {
			applicationIdList: (record.applications ?? []).map((item) => item.id),
			roleIdList: (record.roles ?? []).map((item) => item.id),
		};
		form.setFieldsValue(valuesObj);
	};

	const handleSearch = (input, option) => {
		return option.label.toLowerCase().includes(input.toLowerCase());
	};
	return (
		<div className="app-page-container">
			<div className="app-navbar">
				<div className="app-navbar-text">{localize('userManagement')}</div>
			</div>
			<Modal
				title={localize('selectAppsRoles')}
				open={showEditModal}
				onCancel={roleCancel}
				centered
				width={514}
				className="app-modal-common app-user-max-height"
				closeIcon={<img src={require('assets/images/closeBtn.png')} className="app-modal-close-btn" />}
				footer={[
					<div
						key="roleCancel"
						onClick={roleCancel}
						className="app-btn app-btn-cancel-margin-right app-btn-edit app-btn-padding app-btn-min-width"
					>
						{localize('cancel')}
					</div>,
					<div
						key="save"
						className="app-btn app-btn-primary app-btn-primary app-btn-min-width"
						onClick={throttle(updateUserRolesAndApps, 1000)}
					>
						{localize('save')}
					</div>,
				]}
			>
				<div>
					<div className="user-top-account">
						<span className="user-top-account-name">{localize('users')}</span> : {editingUser?.name ?? ''}
					</div>
					<Form
						form={form}
						layout="vertical"
						initialValues={{
							remember: true,
						}}
						autoComplete="off"
					>
						<Form.Item name="applicationIdList" label={localize('applications')} validateTrigger="onBlur">
							<Select
								mode="multiple"
								allowClear
								className="input-con-all-user"
								filterOption={handleSearch}
								placeholder={localize('selectApplications')}
								value={form.applicationIdList}
								options={applications}
							/>
						</Form.Item>
						<Form.Item name="roleIdList" label={localize('roles')} validateTrigger="onBlur">
							<Select
								mode="multiple"
								allowClear
								className="input-con-all-user"
								filterOption={handleSearch}
								placeholder={localize('selectRoles')}
								value={form.roleIdList}
								options={roles}
							/>
						</Form.Item>
					</Form>
				</div>
			</Modal>
			<div className="app-table">
				<div className="app-table-content" ref={ref}>
					<ConfigProvider renderEmpty={emptyTemplate}>
						<Table
							rowKey={(record) => record.id}
							columns={mainColumns()}
							dataSource={pageList}
							pagination={false}
							scroll={{ x: 800, y: tableHeight }}
						/>
					</ConfigProvider>
				</div>
				<div className="table-line-all">
					<PaginationComponent
						pagination={{
							current: pageIndex,
							pageSize: pageSize,
							total: total,
						}}
						onChange={(current, pageSize) => {
							setPageIndex(current);
							setPageSize(pageSize);
						}}
					/>
				</div>
			</div>
		</div>
	);
};

export default User;
